/* global CVP */

// #### ClientlessAccessEnabler
//
// Mock AccessEnabler for Clientless Auth
//
// Usage:
//     init()

'use strict';

var Util = require('./util');
var Store = require('./store');


var apiOrigin = 'https://api.auth.adobe.com';
var deviceType = 'clientless';
var requestorId = null;
var requestorDomain = 'turner.com';
var localRegCode = null;
var regcodeKey = 'regcode';
var authProviderKey = 'cvp.auth.clientlessProvider';
var loginUrl = '';
var inited = false;

function setRequestor(requestor) {
	if (!requestor) {
		throw new Error('requestorId required!');
	}

	requestorId = requestor;

	// We could obtain JSON by appending '.json' to the URL,
	// or adding query param "format=json" or setting HTTP Accept header,
	// but we're going to use the XML config parsing via `setConfig`.
	CVP.Request({
		url : apiOrigin + '/api/v1/config/' + encodeURI(requestorId),
		success : function (data) {
			window.setConfig(data);
		},
		failure : function () {
			window.setConfig(null);
		}
	});
}

function authNFailure() {
	window.setAuthenticationStatus(0, 'not authenticated');

	// Faking trackingData for DPI
	window.sendTrackingData('authenticationDetection', [
		false,         // 0: Whether the token request was successful (true/false)
		null,          // 1: MVPD ID
		null,          // 2: GUID
		null,          // 3: Token already in cache (true/false)
		'2nd-screen',  // 4: Device Type
		'clientless',  // 5: Access Enabler client Type
		'2nd-screen'   // 6: OS
	]);
}

function authNSuccess() {
	window.setAuthenticationStatus(1, 'authenticated');

	// Faking trackingData for DPI
	var providerData = getSelectedProvider();
	var mvpdId = providerData.MVPD;

	window.sendTrackingData('authenticationDetection', [
		true,          // 0: Whether the token request was successful (true/false)
		mvpdId,        // 1: MVPD ID
		null,          // 2: GUID
		false,         // 3: Token already in cache (true/false)
		'2nd-screen',  // 4: Device Type
		'clientless',  // 5: Access Enabler client Type
		'2nd-screen'   // 6: OS
	]);
}

function logout() {
	// remove the query param at the end of the URL!
	var currentUrl = location.href;
	var newUrl = currentUrl;
	if (location.search) {
		var queryParams = Util.parseQueryString(location.search);
		if (regcodeKey in queryParams) {
			delete queryParams[regcodeKey];
			newUrl = Util.augmentQueryString(location.origin + location.pathname, queryParams);
			if (location.hash) {
				newUrl += location.hash;
			}
		}
	}
	if (newUrl !== currentUrl) {
		location.replace(newUrl);
	}
	else {
		location.reload(true);
	}
}

function checkAuthentication() {
	var queryParams = Util.parseQueryString(location.search);
	var regcode = queryParams[regcodeKey];

	if (regcode) {
		CVP.Request({
			url : apiOrigin + '/api/v1/checkauthn/' + encodeURI(regcode),
			data : {
				'deviceType': deviceType,
				'requestor': requestorId
			},
			success : authNSuccess,
			failure : logout
		});
	}
	else {
		authNFailure();
	}
}

function getAuthentication() {
	window.displayProviderDialog();
}

function createRegcodeSuccessCallback(regcode) {
	return function regcodeSuccessCallback(/*data*/) {
		localRegCode = regcode;
		window.registrationCodeValid(regcode);
		getAuthentication();
	};
}

function checkRegcode(regcode) {
	if (!regcode) {
		window.registrationCodeInvalid(regcode);
		return;
	}

	CVP.Request({
		url : apiOrigin + '/reggie/v1/' + requestorId + '/regcode/' + encodeURI(regcode),
		success : createRegcodeSuccessCallback(regcode),
		failure : CVP.Utils.bind(window.registrationCodeInvalid, null, regcode)
	});
}

function setSelectedProvider(mvpdId) {
	if (mvpdId === null) {
		Store.removeSession(authProviderKey);
		return;
	}

	var mvpd = CVP.AuthManager.getMvpdById(mvpdId);

	if (!mvpd) {
		throw new Error('mvpd was not found!');
	}

	Store.setSession(authProviderKey, mvpdId);

	// Faking trackingData for DPI
	window.sendTrackingData('mvpdSelection', [
		mvpdId,        // 0: Selected MVPD
		'2nd-screen',  // 1: Device Type
		'clientless',  // 2: Access Enabler client Type
		'2nd-screen'   // 3: OS
	]);

	// build auth url
	var redirectUrl = window.location.href;
	redirectUrl += (redirectUrl.indexOf('?') !== -1 ? '&' : '?') + 'regcode=' + encodeURIComponent(localRegCode);

	var initialParams = {
		'reg_code' : localRegCode
	};

	// added 'no_iframe' to enforce consistency after issues from testing multiple providers and auth impls
	var otherParams = {
		'no_iframe' : 'true',
		'noflash' : 'true',
		'deviceType' : deviceType,
		'mso_id' : mvpdId,
		'requestor_id' : requestorId,
		'domain_name' : requestorDomain,
		'redirect_url' : Util.createRedirectUrl(redirectUrl)
	};

	var baseUrl = apiOrigin + '/api/v1/authenticate';

	// https://tve.zendesk.com/entries/23648011-Clientless-Avoid-using-reg-code-in-authenticate-request
	// Insert 'reg_code' first to avoid '&reg_code' being interpreted as '&reg' and thus ®.

	loginUrl = Util.augmentQueryString(Util.augmentQueryString(baseUrl, initialParams), otherParams);

	window.location.href = loginUrl;
}

function getSelectedProvider() {
	var selectedProviderId = Store.getSession(authProviderKey) || null;

	var result = {
		'MVPD' : selectedProviderId,
		'AE_State' : 'unknown'
	};

	return result;
}

function init(cfg) {
	if (inited) {
		return;
	}

	inited = true;

	if ('apiOrigin' in cfg) {
		apiOrigin = cfg.apiOrigin;
	}

	if ('domain' in cfg) {
		requestorDomain = cfg.domain;
	}

	if ('deviceType' in cfg) {
		deviceType = cfg.deviceType;
	}

	window.entitlementLoaded();
}


exports.init = init;
exports.setSelectedProvider = setSelectedProvider;
exports.getSelectedProvider = getSelectedProvider;
exports.checkRegcode = checkRegcode;
exports.checkAuthentication = checkAuthentication;
exports.getAuthentication = getAuthentication;
exports.logout = logout;
exports.setRequestor = setRequestor;
